/* eslint-disable @stylistic/lines-around-comment */
import { authAdminChannel } from "@httpServices/auth"
import { authSignageChannel } from "@httpServices/signage"
import { logger } from "@hooks"
import { isSignagePage } from "@utils/helpers"
import Echo from "laravel-echo"
import Pusher from "pusher-js"
import { ChannelAuthorizerGenerator } from "pusher-js/types/src/core/auth/deprecated_channel_authorizer"
import { ChannelAuthorizationData } from "pusher-js/types/src/core/auth/options"
import { Transport } from "pusher-js/types/src/core/config"

if (typeof window !== "undefined") {
  ;(window as any).Pusher = Pusher
}

export const APP_SOCKET_KEY = process.env.APP_SOCKET_KEY ?? ""

export const APP_SOCKET_HOST = process.env.APP_SOCKET_HOST ?? "localhost"

export const APP_SOCKET_PORT = process.env.APP_SOCKET_PORT ?? "8080"

type ConnectionConfigType = {
  wsHost: string
  wsPort: number
  wssPort: number
  forceTLS: boolean
  enabledTransports: Transport[]
}

function buildConnectionConfig(): ConnectionConfigType {
  return {
    wsHost: APP_SOCKET_HOST,
    wsPort: Number(APP_SOCKET_PORT),
    wssPort: Number(APP_SOCKET_PORT),
    forceTLS: false,
    enabledTransports: ["ws", "wss"],
  }
}

type SocketState = {
  echo: Echo<"reverb"> | null
  channelName: string | null

  /** Pre-fetched auth token keyed by channel name (without "private-" prefix) */
  authTokens: Record<string, ChannelAuthorizationData>
  /** Prevents race conditions when multiple hooks call initializeDeviceSocket simultaneously */
  initializationPromise: Promise<{ echo: Echo<"reverb">; channelName: string }> | null
}

const state: SocketState = {
  echo: null,
  channelName: null,
  authTokens: {},
  initializationPromise: null,
}

/**
 * Fetches the full channel auth response from the appropriate API.
 * - Signage pages: POST broadcasting/signage-auth (uses X-Code-Device header)
 * - Admin pages:   POST broadcasting/auth         (uses Bearer token)
 *
 * Returns the full BroadcastAuthResponse: { channel, client_id, socket_id, auth }
 */
async function fetchChannelAuth(
  socketId: string,
  channelName: string
): Promise<{ authData: ChannelAuthorizationData; channel: string }> {
  const authFunction = isSignagePage() ? authSignageChannel : authAdminChannel
  const response = await authFunction({ socket_id: socketId, channel_name: `private-${channelName}` })

  return { authData: response?.data || response, channel: channelName }
}

/**
 * Custom authorizer that serves pre-fetched auth tokens.
 * Falls back to calling the appropriate auth API if a token for the channel
 * is not cached (should not happen in normal flow, but provides a safety net).
 */
const buildAuthorizer: ChannelAuthorizerGenerator = (channel) => ({
  authorize(socketId: string, callback: (error: Error | null, authData: ChannelAuthorizationData | null) => void) {
    const cleanName = channel.name.replace(/^private-/, "")
    const cachedAuth = state.authTokens[cleanName]

    if (cachedAuth) {
      logger("socket - Using cached auth token for channel:", { cleanName })
      callback(null, cachedAuth)

      return
    }

    // Fallback: fetch auth on-the-fly (e.g. if token was not pre-fetched)
    logger("socket - No cached auth token — fetching on-the-fly for channel:", { cleanName })
    fetchChannelAuth(socketId, cleanName)
      .then((response) => {
        callback(null, response.authData)
      })
      .catch((error: Error) => {
        callback(error, null)
      })
  },
})

function buildEcho(): Echo<"reverb"> {
  const config = buildConnectionConfig()

  return new Echo({
    ...config,
    broadcaster: "reverb",
    key: APP_SOCKET_KEY,
    authorizer: buildAuthorizer,
  })
}

/**
 * Initialize the socket connection following the correct flow:
 *
 * Step 1: Connect WebSocket (Echo/Pusher) → Reverb assigns socket_id on "connected"
 * Step 2: Call the appropriate auth API with { socket_id }
 *         → returns full BroadcastAuthResponse: { channel, client_id, socket_id, auth }
 *         - Signage: POST broadcasting/signage-auth
 *         - Admin:   POST broadcasting/auth (Bearer token sent automatically)
 * Step 3: Cache the auth token so the custom authorizer can serve it when Echo.private() is called
 *
 * The same Echo/Pusher connection is used throughout — no re-connection.
 */
export function initializeDeviceSocket(channelName: string): Promise<{ echo: Echo<"reverb">; channelName: string }> {
  if (state.initializationPromise) {
    return state.initializationPromise
  }

  disconnectSocket()

  state.initializationPromise = (async () => {
    try {
      // Step 1: Build Echo — this starts the WebSocket connection immediately
      const echo = buildEcho()
      const pusher: Pusher = (echo as any).connector?.pusher

      // Step 1 → 2: Wait for connection, then fetch full auth info
      const authInfo = await new Promise<{ authData: ChannelAuthorizationData; channel: string }>((resolve, reject) => {
        const onConnected = async () => {
          const socketId: string = pusher.connection.socket_id

          logger("socket - WebSocket connected — socket_id", { socketId })

          try {
            // Step 2: Fetch full auth response (works for both signage and admin)
            const data = await fetchChannelAuth(socketId, channelName)

            if (!data.channel) {
              reject(new Error("Server did not return a channel."))

              return
            }

            resolve(data)
          } catch (error) {
            reject(error)
          }
        }

        if (pusher.connection.state === "connected") {
          onConnected()
        } else {
          pusher.connection.bind("connected", onConnected)
        }

        pusher.connection.bind("failed", () => {
          reject(new Error("WebSocket connection failed."))
        })
      })

      // Step 3: Cache the auth token and store connection info
      const cleanName = authInfo.channel.replace(/^private-/, "")

      state.authTokens[cleanName] = authInfo.authData
      state.echo = echo
      state.channelName = cleanName

      return { echo, channelName: cleanName }
    } catch (error) {
      state.initializationPromise = null
      throw error
    }
  })()

  return state.initializationPromise
}

export function getSocket(): Echo<"reverb"> | null {
  return state.echo
}

export function getPusherConnection(): Pusher["connection"] | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (state.echo as any)?.connector?.pusher?.connection ?? null
}

export function getChannelName(): string | null {
  return state.channelName
}

export function disconnectSocket(): void {
  if (state.echo) {
    state.echo.disconnect()
    state.echo = null
    state.channelName = null
    state.authTokens = {}
  }

  state.initializationPromise = null
}
